/* eslint-disable react-hooks/exhaustive-deps */
import {
  useMediaQuery,
  Grid,
  Box,
  useTheme,
  Typography,
  Button,
  Container,
  Skeleton,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CustomRightImage from "@components/CustomRightImagev2";
import { useLazyGetTaxSystemConfigQuery } from "@api/applications";
import { useEffect, useState } from "react";
import { FinancedServicesStructure, GuaranteeConfigStruture, TaxSystemConfigResponse } from "@interfaces/index";
import { amountToText, calculateAmountMonthly, formatCurrency } from "@helpers/funciones";
import icon_check from "@assets/img/icon_check_cotizador.svg";
import icon_check_nowport from "@assets/img/now_ports/icon_check_nowports.svg";
import { StyledSlider } from "@components/CustomSlider";
import { ButtonTypeSelected } from "@components/ButtonTypeSelected";
import { CustomDialog } from "@components/CustomDialog";
import IconInfo from "@components/Images/IconInfo";
import * as yup from "yup";
import { useFormik } from "formik";
import { useLazyGetFinancedServicesQuery, useLazyGetGuaranteeConfigQuery } from "@api/catalogs";
import { GUARANTEE_TYPE } from "@helpers/constantes";
import useInterfaceConfig from "@hooks/useInterfaceConfig";

const CotizadorNowPorts = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isShortScreen = useMediaQuery("(max-height:820px)");
  const interfaceConfig = useInterfaceConfig();

  const [getTaxSystemConfig] = useLazyGetTaxSystemConfigQuery();
  const [getFinancedService] = useLazyGetFinancedServicesQuery();
  const [getGuaranteeTypeConfig] = useLazyGetGuaranteeConfigQuery();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [configPM, setConfigPM] = useState<TaxSystemConfigResponse | null>(null);
  const [personType, setPersonType] = useState<string>("");

  const [garantiaType, setGarantiaType] = useState<string>("");
  const [guaranteeType, setGuaranteeType] = useState<string>("");

  const [amountMarks, setAmountMarks] = useState<{ value: number; label: string }[]>([]);
  const [termMarks, setTermMarks] = useState<{ value: number; label: string }[]>([]);
  const [guaranteeConfigListPM, setGuaranteeConfigListPM] = useState<GuaranteeConfigStruture[]>([]);

  const [cotizadorConfig, setCotizadorConfig] = useState<TaxSystemConfigResponse>({
    id: "",
    code: "",
    product_id: "",
    minAmount: 0,
    maxAmount: 0,
    stepAmount: 0,
    minTerm: 0,
    maxTerm: 0,
    stepTerm: 0,
    defaultAmount: 0,
    defaultTerm: 0,
    tax_system_type: "",
    jump_term: 0,
  });
  const [financedServices, setFinancedServices] = useState<FinancedServicesStructure[]>([]);

  const [selectedAmount, setSelectedAmount] = useState<number>(cotizadorConfig.defaultAmount);
  const [selectedTerm, setSelectedTerm] = useState<number>(cotizadorConfig.defaultTerm);
  const [monthlyRate, setMonthlyRate] = useState<number>(0);
  const [showGarantiaActivo, setShowGarantiaActivo] = useState<boolean>(false);

  const [hoverDialogOpen, setHoverDialogOpen] = useState(false);
  const [guaranteeNoDispongo, setGuaranteeNoDispongo] = useState<GuaranteeConfigStruture | null>(null);

  const handleOpenModal = () => setHoverDialogOpen(true);
  const handleCloseModal = () => setHoverDialogOpen(false);

  useEffect(() => {
    setPersonType("PM");
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [responsePM, responseFinancedServices, setGuaranteeConfigList] = await Promise.all([
          getTaxSystemConfig("CSN01").unwrap(),
          getFinancedService().unwrap(),
          getGuaranteeTypeConfig({ tax_code: "CSN01" }).unwrap(),
        ]);
        setConfigPM(responsePM);
        setFinancedServices(responseFinancedServices);
        setGuaranteeConfigListPM(setGuaranteeConfigList);

        setGarantiaType(GUARANTEE_TYPE.cuentas.code);
      } catch (error) {
        console.error("Error fetching tax system configs", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (!guaranteeConfigListPM || guaranteeConfigListPM.length === 0) return;

    const found = guaranteeConfigListPM.find(
      (item) => item.guarantee_type.code === GUARANTEE_TYPE.no_dispongo_de_cuentas.code,
    );

    setGuaranteeNoDispongo(found || null);
  }, [guaranteeConfigListPM]);

  useEffect(() => {
    if (personType === "PM" && configPM) {
      setCotizadorConfig(configPM);
    }
  }, [personType, configPM]);

  useEffect(() => {
    const { minAmount, maxAmount, stepAmount, minTerm, maxTerm, stepTerm, defaultAmount, defaultTerm } =
      cotizadorConfig;

    if (minAmount < maxAmount && stepAmount > 0) {
      const marks = [];

      marks.push({ value: minAmount, label: `${amountToText(minAmount)} USD` });
      marks.push({ value: maxAmount, label: `${amountToText(maxAmount)} USD` });

      setAmountMarks(marks);
    }

    if (minTerm < maxTerm && stepTerm > 0) {
      const marks = [];

      for (let i = minTerm; i <= maxTerm; i += stepTerm) {
        marks.push({ value: i, label: `${i}` });
      }

      setTermMarks(marks);
      setSelectedAmount(defaultAmount);
      setSelectedTerm(defaultTerm);
    }
  }, [cotizadorConfig]);

  const formik = useFormik({
    initialValues: {
      services: "",
      isClient: "",
      guarantee_type: "",
    },
    validationSchema: yup.object().shape({
      services: yup.string().required("Campo requerido"),
      isClient: yup.string().required("Campo requerido"),
      guarantee_type: yup.string().required("Campo requerido"),
    }),
    onSubmit: async (values) => {
      console.log(values);
      setConditions();
    },
  });

  useEffect(() => {
    if (selectedAmount && selectedTerm) {
      const monthly = calculateAmountMonthly(selectedAmount, selectedTerm, interfaceConfig?.interest_rate);
      setMonthlyRate(monthly);
    }
  }, [selectedAmount, selectedTerm]);

  const setConditions = () => {
    if (!cotizadorConfig.product_id || !selectedAmount || !selectedAmount || !monthlyRate) {
      return;
    }
    const sourceCode = sessionStorage.getItem("source_code");
    let urlPortal = `/producto/creditosimple/crear_cuenta?tax_system_id=${cotizadorConfig.id}&product_id=${cotizadorConfig.product_id}&person_type=${cotizadorConfig.code}&loan_amount=${selectedAmount}&loan_term=${selectedTerm}&tax_system_type=${cotizadorConfig.tax_system_type}&financed_services_id=${formik.values.services}&is_previous_client=${formik.values.isClient}&guarantee_type_id=${formik.values.guarantee_type}`;
    if (sourceCode) {
      urlPortal += `&source_code=${sourceCode}`;
    }
    navigate(urlPortal);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sourceCode = params.get("code");

    if (sourceCode) {
      sessionStorage.setItem("source_code", sourceCode);
    } else {
      sessionStorage.removeItem("source_code");
    }
  }, []);

  useEffect(() => {
    if (garantiaType === GUARANTEE_TYPE.no_dispongo_de_cuentas.code) {
      const selectedGuarantee = guaranteeConfigListPM.find((item) => item.guarantee_type.code === garantiaType);
      const id_child = selectedGuarantee?.childrens?.find((item) => item.code === guaranteeType)?.id;

      formik.setFieldValue("guarantee_type", id_child);
    } else if (garantiaType === GUARANTEE_TYPE.cuentas.code) {
      const selectedGuarantee = guaranteeConfigListPM.find(
        (item) => item.guarantee_type.code === garantiaType,
      )?.guarantee_type_id;

      formik.setFieldValue("guarantee_type", selectedGuarantee);
    }
  }, [guaranteeType, garantiaType]);

  return (
    <>
      <Grid container>
        <Grid item xs={12} md={8}>
          <Container
            sx={{
              py: isShortScreen ? 2 : 4,
            }}
            maxWidth={"sm"}
          >
            <Typography
              fontWeight={500}
              variant="h3"
              mt={isShortScreen ? 1 : 2}
              fontSize={isShortScreen ? "1.5rem" : "2rem"}
              color={theme?.palette?.primary?.dark}
              textAlign={"center"}
            >
              Cotizador
            </Typography>

            <Box mx={isMobileOrTablet ? 4 : 0}>
              {/* Selección de monto */}
              <Box mt={isShortScreen ? 1 : 2}>
                <Typography
                  fontWeight={500}
                  fontSize={isShortScreen ? "14px" : "16px"}
                  color={theme?.palette?.primary?.main}
                  textAlign={"center"}
                >
                  Selecciona el monto
                </Typography>

                <Typography
                  fontWeight={700}
                  fontSize={isShortScreen ? "12px" : "13px"}
                  mt={isShortScreen ? 1 : 2}
                  color={theme?.palette?.primary?.dark}
                  textAlign={"center"}
                >
                  {amountToText(selectedAmount)} USD
                </Typography>

                {!isLoading ? (
                  <StyledSlider
                    aria-label="amount"
                    min={cotizadorConfig.minAmount}
                    max={cotizadorConfig.maxAmount}
                    step={cotizadorConfig.stepAmount}
                    marks={amountMarks}
                    defaultValue={cotizadorConfig.defaultAmount}
                    value={selectedAmount}
                    onChange={(_event, newValue) => setSelectedAmount(newValue as number)}
                  />
                ) : (
                  <Skeleton variant="rectangular" animation="wave" />
                )}
              </Box>

              {/* Plazo máximo */}
              <Box mb={1}>
                <Typography
                  fontWeight={500}
                  fontSize={isShortScreen ? "14px" : "16px"}
                  mb={isShortScreen ? 2 : 3}
                  color={theme?.palette?.primary?.main}
                  textAlign={"center"}
                >
                  Selecciona el plazo máximo de disposición
                </Typography>

                {!isLoading ? (
                  <StyledSlider
                    aria-label="term"
                    min={cotizadorConfig.minTerm}
                    max={cotizadorConfig.maxTerm}
                    step={cotizadorConfig.stepTerm}
                    defaultValue={cotizadorConfig.defaultTerm}
                    marks={termMarks}
                    value={selectedTerm}
                    onChange={(_event, newValue) => setSelectedTerm(newValue as number)}
                    valueLabelDisplay="on"
                  />
                ) : (
                  <Skeleton variant="rectangular" animation="wave" />
                )}
              </Box>

              {/* Tipo de Garantía */}
              <Box>
                <Typography
                  fontWeight={500}
                  fontSize={isShortScreen ? "14px" : "16px"}
                  color={theme?.palette?.primary?.dark}
                  textAlign={"center"}
                >
                  Selecciona tu tipo de Garantía{" "}
                  <IconInfo fill={theme.palette.primary.main} onClick={handleOpenModal} style={{ cursor: "pointer" }} />
                </Typography>

                <Grid container spacing={isShortScreen ? 1 : 2} mt={1} mb={isShortScreen ? 1 : 2}>
                  <Grid item xs={6} md={6} display="flex" justifyContent={"flex-end"}>
                    {!isLoading ? (
                      <ButtonTypeSelected
                        title="Cuentas por cobrar"
                        onClick={() => {
                          setGarantiaType(GUARANTEE_TYPE.cuentas.code);
                          setShowGarantiaActivo(false);
                          setGuaranteeType("");
                        }}
                        isSelected={garantiaType === GUARANTEE_TYPE.cuentas.code}
                        isMovile={isMobileOrTablet}
                        height={isShortScreen ? "60px" : "70px"}
                        heightMoviles="60px"
                        width={isShortScreen ? "170px" : "190px"}
                        widthMoviles="150px"
                        icon={icon_check}
                        width_icon="22px"
                        height_icon="22px"
                        font_size={isShortScreen ? "12px" : "14px"}
                        top_icon="-12px"
                        font_size_movil="11px"
                      />
                    ) : (
                      <Skeleton
                        variant="rectangular"
                        animation="wave"
                        width={isMobileOrTablet ? 150 : 190}
                        height={isMobileOrTablet ? 60 : 70}
                      />
                    )}
                  </Grid>
                  <Grid item xs={6} md={6} display="flex" justifyContent={"flex-start"}>
                    {!isLoading ? (
                      <ButtonTypeSelected
                        title="No dispongo de cuentas por cobrar"
                        onClick={() => {
                          setGarantiaType(GUARANTEE_TYPE.no_dispongo_de_cuentas.code);
                          setShowGarantiaActivo(true);
                        }}
                        isSelected={garantiaType === GUARANTEE_TYPE.no_dispongo_de_cuentas.code}
                        isMovile={isMobileOrTablet}
                        height={isShortScreen ? "60px" : "70px"}
                        heightMoviles="60px"
                        width={isShortScreen ? "170px" : "190px"}
                        widthMoviles="150px"
                        icon={icon_check}
                        width_icon="22px"
                        height_icon="22px"
                        font_size={isShortScreen ? "12px" : "14px"}
                        top_icon="-12px"
                        font_size_movil="11px"
                      />
                    ) : (
                      <Skeleton
                        variant="rectangular"
                        animation="wave"
                        width={isMobileOrTablet ? 150 : 190}
                        height={isMobileOrTablet ? 60 : 70}
                      />
                    )}
                  </Grid>
                </Grid>
              </Box>

              {/* Garantía Activo */}
              {showGarantiaActivo && (
                <Box mt={isShortScreen ? 2 : 4}>
                  <Typography
                    fontWeight={500}
                    fontSize={isShortScreen ? "14px" : "16px"}
                    mt={isShortScreen ? 1 : 2}
                    color={theme?.palette?.primary?.dark}
                    textAlign={"center"}
                  >
                    Selecciona tu Garantía
                  </Typography>
                  <Grid container spacing={0} mt={isShortScreen ? 1 : 2}>
                    {!isLoading
                      ? guaranteeNoDispongo?.childrens?.map((child) => (
                          <Grid item xs={3} md={3} key={child.id} display="flex" justifyContent={"center"}>
                            <ButtonTypeSelected
                              title={child.name}
                              onClick={() => {
                                setGuaranteeType(child.code);
                              }}
                              isSelected={guaranteeType === child.code}
                              isMovile={isMobileOrTablet}
                              height="60px"
                              heightMoviles="60px"
                              width={isShortScreen ? "120px" : "140px"}
                              widthMoviles="80px"
                              icon={icon_check_nowport}
                              width_icon="20px"
                              height_icon="20px"
                              font_size={isShortScreen ? "12px" : "14px"}
                              top_icon="-11px"
                              font_size_movil="10px"
                            />
                          </Grid>
                        ))
                      : Array.from({ length: 4 }).map((_, index) => (
                          <Grid item xs={3} md={3} key={index} display="flex" justifyContent={"center"}>
                            <Skeleton
                              variant="rectangular"
                              animation="wave"
                              width={isMobileOrTablet ? 150 : 190}
                              height={isMobileOrTablet ? 60 : 70}
                            />
                          </Grid>
                        ))}
                  </Grid>
                </Box>
              )}

              {/* Formulario */}
              <form onSubmit={formik.handleSubmit} noValidate>
                <FormControl
                  fullWidth
                  variant="outlined"
                  sx={{ mt: isShortScreen ? 1 : 2 }}
                  error={formik.touched.services && Boolean(formik.errors.services)}
                >
                  <InputLabel id="services-label" required>
                    ¿Qué servicio deseas financiar?
                  </InputLabel>
                  <Select
                    labelId="services-label"
                    id="services"
                    name="services"
                    value={formik.values.services}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    disabled={formik.isSubmitting}
                    label="¿Qué servicio deseas financiar?"
                    MenuProps={{
                      PaperProps: {
                        sx: { maxHeight: 220, overflowY: "auto" },
                      },
                    }}
                  >
                    <MenuItem value="">Selecciona</MenuItem>
                    {financedServices.map((service) => (
                      <MenuItem key={service.id} value={service.id}>
                        {service.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.services && formik.errors.services && (
                    <FormHelperText>{formik.errors.services}</FormHelperText>
                  )}
                </FormControl>

                <FormControl
                  fullWidth
                  sx={{ mt: isShortScreen ? 1 : 2 }}
                  variant="outlined"
                  error={formik.touched.isClient && Boolean(formik.errors.isClient)}
                >
                  <InputLabel id="isClient-label" required>
                    ¿Ya eres cliente de Nowports?
                  </InputLabel>
                  <Select
                    labelId="isClient-label"
                    id="isClient"
                    name="isClient"
                    fullWidth
                    required
                    value={formik.values.isClient}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    disabled={formik.isSubmitting}
                    label="¿Ya eres cliente de Nowports?"
                  >
                    <MenuItem value="">Selecciona</MenuItem>
                    <MenuItem value="true">Sí</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>

                  {formik.touched.isClient && formik.errors.isClient && (
                    <FormHelperText>{formik.errors.isClient}</FormHelperText>
                  )}
                </FormControl>

                <Typography
                  fontWeight={700}
                  fontSize={isShortScreen ? "14px" : "16px"}
                  mt={isShortScreen ? 1 : 2}
                  mb={isShortScreen ? 1 : 2}
                  color={theme?.palette?.primary?.main}
                  textAlign={"center"}
                >
                  Pago mensual estimado*
                </Typography>

                <Typography
                  mb={isShortScreen ? 1.5 : 3}
                  textAlign={"center"}
                  variant="h4"
                  fontWeight={theme?.palette?.fontWeights?.semibold}
                  color={theme?.palette?.primary?.dark}
                >
                  {isNaN(monthlyRate) || !isFinite(monthlyRate) ? "0" : formatCurrency(monthlyRate)}{" "}
                  <Typography
                    variant="h5"
                    component={"span"}
                    fontWeight={theme?.palette?.fontWeights?.semibold}
                    color={theme?.palette?.primary?.dark}
                  >
                    USD
                  </Typography>
                </Typography>

                <Box display="flex" justifyContent="center">
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={isLoading}
                    sx={{
                      fontSize: isShortScreen ? "0.8rem" : "1rem",
                    }}
                  >
                    Continuar
                  </Button>
                </Box>
              </form>

              <Typography
                textAlign={"center"}
                fontWeight={theme?.palette?.fontWeights?.light}
                color={theme?.palette?.primary?.main}
                mt={isShortScreen ? 1 : 2}
                mb={isShortScreen ? 1 : 2}
                fontSize={"13px"}
              >
                <small>*Pago estimado asumiendo disposición total y tasa 23% (18% + 5% SOFR).</small>
              </Typography>
            </Box>
          </Container>
        </Grid>

        {!isMobileOrTablet && (
          <Grid item xs={12} md={4}>
            <CustomRightImage />
          </Grid>
        )}
      </Grid>

      <CustomDialog
        colorClose={theme?.palette?.primary?.light}
        open={hoverDialogOpen}
        onClose={handleCloseModal}
        itemTitle={
          <Typography fontWeight={700} fontSize={"16px"} color={theme?.palette?.primary?.main} textAlign={"center"}>
            Garantías
          </Typography>
        }
        itemContent={
          <>
            <Typography fontWeight={300} fontSize={"16px"} color={theme?.palette?.primary?.dark} textAlign={"center"}>
              Garantía desde 1.2:1 <br />
              <br />
              Puedes usar cuentas por cobrar como garantía. Si no cuentas con ellas, puedes aportar activo fijo
              (inmuebles, maquinaria, vehículos o mobiliario).
            </Typography>
          </>
        }
      />
    </>
  );
};
export default CotizadorNowPorts;
